/**
 * DashboardPage — Task 25.8 (DEC-119). PLATFORM_ADMIN console landing page (/).
 *
 * An at-a-glance platform overview composed from one aggregate call:
 *   Row 1 — 4 health cards (haulers, catalog, OCC sync, HaulId ready)
 *   Row 2 — compact 5-agency sync status (read-only) + link to /catalog-sync
 *   Row 3 — abbreviation health (missing haulers link to their tenant detail)
 *   Row 4 — recent 5 sync runs
 *   Row 5 — pending catalog-change banner
 */
import * as React from 'react';
import { SyncErrorText } from '../catalog-sync/SyncErrorText';
import { useLocation, useNavigate } from 'react-router-dom';
import { listFrom } from '../../lib/url-state';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Pill, subText } from '../../ui/bits';
import { useDashboard, type DashboardData } from './hooks';
import { runDurationLabel } from '../catalog-sync/run-duration';
import { formatNumber } from '@lodex-pro/shared/number-format';

const AGENCY_STATE: Record<string, string> = {
  OCC: 'Oklahoma',
  RRC: 'Texas',
  WOGCC: 'Wyoming',
  NMOCD: 'New Mexico',
  NDIC: 'North Dakota',
};

function fmtDateTime(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function timeAgo(iso: string | null): string {
  if (iso === null) return 'never';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)} day${Math.round(hours / 24) === 1 ? '' : 's'} ago`;
}

function statusTone(status: string | null): 'accent' | 'positive' | 'danger' | 'warning' | 'muted' {
  switch (status) {
    case 'RUNNING':
      return 'accent';
    case 'SUCCESS':
      return 'positive';
    case 'FAILED':
      return 'danger';
    case 'PARTIAL':
      return 'warning';
    default:
      return 'muted';
  }
}

function HealthCard({
  title,
  value,
  sub,
  badge,
  ok,
}: {
  title: string;
  value: string;
  sub?: React.ReactNode | undefined;
  badge?: React.ReactNode | undefined;
  ok?: boolean | undefined;
}): React.JSX.Element {
  return (
    <div
      style={{
        flex: 1,
        minWidth: '220px',
        backgroundColor: 'var(--color-bg-secondary)',
        border: ok === false ? '1px solid rgba(246, 182, 94,0.4)' : 'none',
        borderRadius: 'var(--radius-card)',
        padding: '16px',
      }}
    >
      <div style={{ ...subText, textTransform: 'none', marginBottom: '6px' }}>{title}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '22px', fontWeight: 700 }}>{value}</span>
        {badge}
      </div>
      {typeof sub === 'string' && (
        <div style={{ ...subText, marginTop: '4px', textTransform: 'none' }}>{sub}</div>
      )}
      {sub !== undefined && typeof sub !== 'string' && sub}
    </div>
  );
}

/** A link; with `cta`, the screen's one call to action (an orange pill, restyle 2026-10-28). */
function ViewLink({
  label,
  to,
  cta = false,
}: {
  label: string;
  to: string;
  cta?: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={(): void => void navigate(to)}
      style={
        cta
          ? {
              flexShrink: 0,
              height: '40px',
              padding: '0 20px',
              border: 'none',
              borderRadius: 'var(--radius-pill)',
              backgroundColor: 'var(--color-cta)',
              color: 'var(--color-on-cta)',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
            }
          : {
              background: 'none',
              border: 'none',
              color: 'var(--color-link)',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: 500,
              padding: 0,
            }
      }
    >
      {label}
    </button>
  );
}

function AbbreviationHealth({ d }: { d: DashboardData }): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const a = d.abbreviations;
  const allSet = a.haulIdReady && a.operatorsMissing === 0;

  if (allSet) {
    return (
      <p style={{ color: 'var(--color-success)', fontSize: '14px' }}>
        ✓ All abbreviations configured
      </p>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {a.haulersMissingList.length > 0 && (
        <div>
          <div style={{ ...subText, textTransform: 'none', marginBottom: '4px' }}>
            {a.haulersMissing} hauler(s) missing an abbreviation:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {a.haulersMissingList.map((h) => (
              <button
                key={h.id}
                type="button"
                onClick={(): void =>
                  void navigate(`/tenants/${h.id}`, { state: listFrom(location) })
                }
                style={{
                  background: 'var(--color-bg-tertiary)',
                  border: '1px solid var(--color-border-secondary)',
                  borderRadius: 'var(--radius-pill)',
                  padding: '4px 10px',
                  color: 'var(--color-text-primary)',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                {h.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {a.operatorsMissing > 0 && (
        <div style={{ fontSize: '13px' }}>
          <span style={subText}>{a.operatorsMissing} operator(s) missing an abbreviation · </span>
          <ViewLink label="Manage abbreviations" to="/abbreviations" />
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section style={{ marginTop: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}
      >
        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export function DashboardPage(): React.JSX.Element {
  const { data: d, isLoading, isError } = useDashboard();

  if (isLoading) return <p style={{ ...subText, padding: '24px' }}>Loading platform health…</p>;
  if (isError || d === undefined) {
    return (
      <p role="alert" style={{ color: 'var(--color-danger)', padding: '24px' }}>
        Could not load the dashboard. Refresh to try again.
      </p>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1150px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 16px' }}>Platform Health</h2>

      {/* Row 1 — health cards */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <HealthCard
          title="Active Haulers"
          value={String(d.tenants.active)}
          sub={`${d.tenants.suspended} suspended · ${d.tenants.setupComplete} setup complete`}
        />
        <HealthCard
          title="Platform Catalog"
          value={`${d.catalog.wells.active.toLocaleString()} wells`}
          sub={`${d.catalog.operators.active.toLocaleString()} operators · ${d.catalog.disposalWells.toLocaleString()} disposal · ${d.catalog.tankCatalog} tank types`}
        />
        <HealthCard
          title="OCC Sync"
          value={timeAgo(d.sync.lastSyncAt)}
          badge={
            d.sync.lastSyncStatus !== null ? (
              <Pill label={d.sync.lastSyncStatus} tone={statusTone(d.sync.lastSyncStatus)} />
            ) : undefined
          }
          sub={
            d.sync.lastSyncError !== null ? (
              <SyncErrorText error={d.sync.lastSyncError} linkToCatalogSync />
            ) : undefined
          }
          ok={d.sync.lastSyncStatus !== 'FAILED'}
        />
        <HealthCard
          title="HaulId Ready"
          value={`${formatNumber(d.abbreviations.haulersReady)} of ${formatNumber(d.abbreviations.haulersTotal)}`}
          badge={
            <Pill
              label={d.abbreviations.haulIdReady ? 'Ready' : 'Attention'}
              tone={d.abbreviations.haulIdReady ? 'positive' : 'warning'}
            />
          }
          sub="haulers ready"
          ok={d.abbreviations.haulIdReady}
        />
      </div>

      {/* Row 2 — sync status per agency (compact, read-only) */}
      <Section
        title="Catalog Sync Status"
        action={<ViewLink label="View details" to="/catalog-sync" />}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '12px',
          }}
        >
          {d.syncAgencies.map((s) => (
            <div
              key={s.stateAgency}
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-card)',
                padding: '12px',
              }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span style={{ fontWeight: 600, fontSize: '14px' }}>
                  {s.stateAgency}
                  <span style={{ ...subText, marginLeft: '6px' }}>
                    {AGENCY_STATE[s.stateAgency]}
                  </span>
                </span>
                <Pill
                  label={s.isActive ? 'Active' : 'Off'}
                  tone={s.isActive ? 'positive' : 'muted'}
                />
              </div>
              <div style={{ ...subText, marginTop: '6px', textTransform: 'none' }}>
                Last {timeAgo(s.lastSyncAt)}
                {s.lastSyncStatus !== null ? ` · ${s.lastSyncStatus}` : ''}
              </div>
              <div style={{ ...subText, textTransform: 'none' }}>
                Next {fmtDateTime(s.nextSyncAt)}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Row 3 — abbreviation health */}
      <Section title="Abbreviation Health">
        <AbbreviationHealth d={d} />
      </Section>

      {/* Row 4 — recent sync activity */}
      <Section
        title="Recent Sync Activity"
        action={<ViewLink label="View all" to="/catalog-sync" />}
      >
        {d.recentActivity.length === 0 ? (
          <p style={subText}>No sync runs yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agency</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Records Added</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.recentActivity.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.stateAgency}</TableCell>
                  <TableCell style={subText}>{fmtDateTime(r.startedAt)}</TableCell>
                  <TableCell>{runDurationLabel(r)}</TableCell>
                  <TableCell>
                    <Pill label={r.status ?? 'UNKNOWN'} tone={statusTone(r.status)} />
                  </TableCell>
                  <TableCell>{r.recordsAdded ?? 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      {/* Row 5 — pending catalog changes */}
      <Section title="Pending Catalog Changes">
        {d.sync.pendingChanges > 0 ? (
          <div
            role="alert"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
              backgroundColor: 'var(--color-bg-secondary)',
              borderRadius: 'var(--radius-card-lg)',
              padding: '14px 20px',
              fontSize: '16px',
            }}
          >
            <span>{d.sync.pendingChanges} operator change(s) awaiting Paelode review.</span>
            <ViewLink label="Review now" to="/catalog-sync" cta />
          </div>
        ) : (
          <p style={{ color: 'var(--color-success)', fontSize: '14px' }}>
            ✓ No pending catalog changes
          </p>
        )}
      </Section>
    </div>
  );
}
